import { toast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useTranslationDeleteMutation } from "@/v2-backoffice-client/translation/orphaned/translation.delete.mutation"

export type TranslationDeleteActionProps = {
  // Row shape of TranslationOrphanedTable; only the code is needed.
  selectedRows: { code: string }[]
  onComplete?(): void
}

// Selection-toolbar action for "Missing in code": soft-delete the selected
// database records (their code usage is gone). Soft delete keeps the temporal
// history; a wrongly deleted feature can be restored via
// api.Upsert_Common_Feature @isDeleted = 0.
function TranslationDeleteAction(props: TranslationDeleteActionProps) {
  const { selectedRows, onComplete } = props

  const [remove, removeState] = useTranslationDeleteMutation()

  const codes = selectedRows.map((row) => row.code)

  async function onConfirm() {
    try {
      const res = await remove({ variables: { codes, isDeleted: true } })
      const results = res.data?.setFeaturesDeleted ?? []
      const failed = results.filter((result) => !result.Ok)
      if (failed.length === 0) {
        toast({ title: `Deleted ${results.length} text(s) (soft delete).` })
      } else {
        toast({
          variant: "destructive",
          title: `Deleted ${results.length - failed.length} text(s), ${failed.length} failed`,
          description: `${failed[0].Code}: ${failed[0].Error ?? "unknown error"}`,
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error instanceof Error ? error.message : String(error),
      })
    }
    onComplete?.()
  }

  return (
    <ConfirmButton
      variant="destructive"
      isStacked
      iconSrc="trash"
      disabled={removeState.loading || codes.length === 0}
      isLoading={removeState.loading}
      content="Soft-delete the selected texts from the translation database. History is kept and a row can be restored, but the texts stop being published."
      confirmContent={`Delete ${codes.length} text(s) from the translation database? This is a soft delete — history is kept.`}
      confirmVariant="destructive"
      onConfirm={onConfirm}
      data-feature-code="UI_BackofficeTranslation_Orphaned_Delete"
    >
      Delete
    </ConfirmButton>
  )
}

export default TranslationDeleteAction
